import type { InputFill } from "../../../api/content/authoring/processAuthoringApi";
import type { ValueKind } from "../../../api/content/authoring/selectorDecoders";
import { boundPlacement, selectorVersionKey, type BoundDefinitions, type OutputPartReference, type ProcessCatalogue, type StepSummary, type WorkflowDefinition } from "../../../api/content/processDefinitions";
import { sourceItemKind } from "./graphSources";
import { isPlacementNode, isSourceNode, type GraphEdge, type GraphNode, type GraphPort, type PortNeed, type PortSide } from "./workflowGraph";
import type { Binding } from "./workflowDraft";

/**
 * The named ports of the graph's cards (processes.md P-14): every edge runs from a named
 * output to a named input, so a card says what it takes and what it gives. A step's ports
 * are those module B serves with its version (`StepSummary.ports`, as the editor binds
 * them); a source the steps read has one output. Inputs filled from the band above sit on
 * the card's top edge, inputs filled by an earlier pipeline step, or by nothing, on its left
 * edge; outputs sit on the right edge of a pipeline card and on the bottom edge of an input.
 * Pure.
 */

/** A card's handle for an input slot or an output part, the same in the view and the editor. */
export const portHandle = {
  input: (slot: string) => `in:${slot}`,
  output: (part: string) => `out:${part}`,
} as const;

export function portOfHandle(handle: string | null | undefined, side: "in" | "out"): string | null {
  return handle?.startsWith(`${side}:`) === true ? handle.slice(side.length + 1) : null;
}

/** A line drawn on the canvas, from a card's output handle to another's input handle. */
export interface DrawnConnection {
  readonly source: string;
  readonly target: string;
  readonly sourceHandle?: string | null;
  readonly targetHandle?: string | null;
}

/** The binding a drawn line asks for: input `slot` of step `key` from `from`; `null` if it joins no output to an input. */
export function bindingOfConnection(connection: DrawnConnection): { readonly key: string; readonly slot: string; readonly from: Binding } | null {
  const part = portOfHandle(connection.sourceHandle, "out");
  const slot = portOfHandle(connection.targetHandle, "in");
  return part === null || slot === null ? null : { key: connection.target, slot, from: { from_step: connection.source, part } };
}

/** The one output of a source (an artifact, a selection, ontology terms, an item). */
export const SOURCE_OUTPUT = "out";

/**
 * Every agent step gives its `answer` as text; it is drawn only where a later step uses
 * it, so that the card shows what the step is for.
 */
const SHOWN_WHEN_USED: ReadonlySet<string> = new Set(["answer"]);

interface Declared {
  readonly name: string;
  readonly kinds: readonly ValueKind[];
  readonly need: PortNeed;
  /** A selector read: drawn only where a source node feeds it. */
  readonly read: boolean;
}

/**
 * The ports of each node, once the edges are known: an input's side is where its edge comes
 * from, an output is marked used or not, and parts an edge names but the definition does
 * not list (a tool that could not be read) are added, so that every edge has both ends.
 */
export function nodePorts(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  catalogue: ProcessCatalogue,
  allOutputs: boolean,
  fillOf: (node: GraphNode, slot: string) => InputFill | null,
): ReadonlyMap<string, readonly GraphPort[]> {
  const into = new Map<string, GraphEdge[]>();
  const outOf = new Map<string, GraphEdge[]>();
  for (const edge of edges) {
    if (edge.targetPort !== null) {
      into.set(edge.target, [...(into.get(edge.target) ?? []), edge]);
    }
    if (edge.sourcePort !== null) {
      outOf.set(edge.source, [...(outOf.get(edge.source) ?? []), edge]);
    }
  }
  const sources = new Map(nodes.filter(isSourceNode).map((node) => [node.id, sourceItemKind(node)]));
  const ports = new Map<string, readonly GraphPort[]>();
  for (const node of nodes) {
    const read = (edge: GraphEdge | undefined) => (edge === undefined ? null : (sources.get(edge.source) ?? null));
    ports.set(node.id, portsOf(node, into.get(node.id) ?? [], outOf.get(node.id) ?? [], catalogue, allOutputs, read, (slot) => fillOf(node, slot)));
  }
  return ports;
}

function portsOf(
  node: GraphNode,
  incoming: readonly GraphEdge[],
  outgoing: readonly GraphEdge[],
  catalogue: ProcessCatalogue,
  allOutputs: boolean,
  read: (edge: GraphEdge | undefined) => GraphPort["reads"],
  fillOf: (slot: string) => InputFill | null,
): GraphPort[] {
  const isStep = isPlacementNode(node);
  const ports: GraphPort[] = [];
  const add = (port: GraphPort) => {
    if (!ports.some((existing) => existing.id === port.id)) {
      ports.push(port);
    }
  };
  for (const input of node.kind === "step" && node.step !== null ? inputOrder(node.step, catalogue) : []) {
    const id = portHandle.input(input.name);
    const edge = incoming.find((candidate) => candidate.targetPort === id);
    if (!input.read || edge !== undefined) {
      add({ id, direction: "input", name: input.name, side: inputSide(edge), kinds: input.kinds, need: input.need, connected: edge !== undefined, filledBy: fillOf(input.name), reads: read(edge), bindable: !input.read });
    }
  }
  for (const edge of incoming) {
    if (edge.targetPort !== null) {
      const name = portOfHandle(edge.targetPort, "in") ?? edge.targetPort;
      add({ id: edge.targetPort, direction: "input", name, side: inputSide(edge), kinds: [], need: "undeclared", connected: true, filledBy: fillOf(name), reads: read(edge), bindable: edge.sourcePort !== portHandle.output(SOURCE_OUTPUT) });
    }
  }
  const outputSide: PortSide = node.band === "main" ? "right" : "bottom";
  // A source gives what it holds: its tooltip says so, as its card does (F61).
  const holds = isSourceNode(node) ? sourceItemKind(node) : null;
  for (const output of declaredOutputs(node, catalogue)) {
    const id = portHandle.output(output.name);
    const connected = outgoing.some((edge) => edge.sourcePort === id);
    if (connected || allOutputs || !SHOWN_WHEN_USED.has(output.name)) {
      add({ id, direction: "output", name: output.name, side: outputSide, kinds: output.kinds, need: null, connected, filledBy: null, reads: holds, bindable: isStep });
    }
  }
  for (const edge of outgoing) {
    if (edge.sourcePort !== null) {
      add({ id: edge.sourcePort, direction: "output", name: portOfHandle(edge.sourcePort, "out") ?? edge.sourcePort, side: outputSide, kinds: [], need: null, connected: true, filledBy: null, reads: null, bindable: isStep });
    }
  }
  return ports;
}

/**
 * A step's inputs in the order its definition mentions them: placeholders and tool inputs
 * first (a selector read is an input too, named by its placeholder), then slots no
 * placeholder names. A selector read's type is the value kind its selector version renders,
 * as the step catalogue says (text where it renders only text); unknown if the catalogue
 * names no such version.
 */
function inputOrder(step: StepSummary, catalogue: ProcessCatalogue): Declared[] {
  const slots = Object.entries(step.ports?.inputs ?? {}).map(([name, slot]): Declared => ({ name, kinds: slot.accepts, need: slot.required ? "required" : "optional", read: false }));
  const reads = new Map(step.selectorReads.map((read) => [read.input, catalogue.selectorValueKinds.get(selectorVersionKey(read.selector))] as const));
  const definition = step.definition;
  const sources = definition.kind === "agent" ? definition.placeholders : definition.kind === "tool" ? definition.inputs : {};
  const ordered: Declared[] = [];
  for (const [name, source] of Object.entries(sources)) {
    const slot = source.source === "input" ? slots.find((candidate) => candidate.name === source.input) : undefined;
    const read = reads.get(name);
    const port: Declared | undefined = slot ?? (reads.has(name) ? { name, kinds: read === undefined ? [] : [read ?? "text"], need: "required", read: true } : undefined);
    if (port !== undefined && !ordered.some((existing) => existing.name === port.name)) {
      ordered.push(port);
    }
  }
  return [...ordered, ...slots.filter((slot) => !ordered.some((existing) => existing.name === slot.name))];
}

/** The output parts a node declares, its own result first: what the step is for. */
function declaredOutputs(node: GraphNode, catalogue: ProcessCatalogue): Declared[] {
  switch (node.kind) {
    case "step": {
      if (node.step === null) {
        return [];
      }
      const outputs = Object.entries(node.step.ports?.outputs ?? {});
      const isResult = ([name]: readonly [string, unknown]) => name === "result" || name === "choice";
      return [...outputs.filter(isResult), ...outputs.filter((output) => !isResult(output))].map(([name, kinds]) => ({ name, kinds, need: "optional", read: false }));
    }
    case "sub_workflow": {
      const inner = node.workflow;
      return node.expanded || inner === null ? [] : Object.entries(inner.exports).map(([name, exported]) => ({ name, kinds: exportedKinds(inner, node.bound, exported, catalogue), need: "optional", read: false }));
    }
    case "abstract_step":
      // No definition, so no declared parts: what later steps read of it is drawn from their edges.
      return [];
    case "artifact":
    case "selection":
    case "terms":
    case "item":
      return [{ name: SOURCE_OUTPUT, kinds: [], need: "optional", read: false }];
  }
}

/**
 * What a collapsed sub-workflow's export gives: the part of the inner step it exports, followed
 * through nested sub-workflows' exports and the definitions their uses bind as the runner flattens
 * them (P-13, ADR-0043 D5). A pin names one exact version, so the chain ends; a Workflow already on
 * it gives nothing rather than loop. An unbound abstract step gives nothing known.
 */
function exportedKinds(inner: WorkflowDefinition, bound: BoundDefinitions, exported: OutputPartReference, catalogue: ProcessCatalogue, seen: ReadonlySet<string> = new Set([inner.reference])): readonly ValueKind[] {
  const placed = inner.steps.find((candidate) => candidate.key === exported.fromStep);
  const use = placed === undefined ? undefined : boundPlacement(placed, bound, (reference) => catalogue.workflows.has(reference));
  if (use?.kind === "workflow") {
    const nested = catalogue.workflows.get(use.workflow);
    const next = nested?.exports[exported.part];
    return nested === undefined || next === undefined || seen.has(nested.reference) ? [] : exportedKinds(nested, use.definitions, next, catalogue, new Set([...seen, nested.reference]));
  }
  const step = use?.kind === "step" ? catalogue.steps.get(use.step) : undefined;
  return step?.ports?.outputs?.[exported.part] ?? [];
}

/** Top when the band above fills it; left when an earlier pipeline step does, or nothing. */
function inputSide(edge: GraphEdge | undefined): PortSide {
  return edge?.route === "feed" ? "top" : "left";
}
